import { create } from 'zustand';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  AuthError
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

interface UserData {
  email: string;
  waterIntake: number;
  dailyGoal: number;
  streak: number;
  lastLogin: Date;
  reminderInterval: number;
  lastCheckDate: string;
  checkInDates: string[];
  monthlyStats: { [key: string]: number };
  drinkHistory: Array<{
    id: string;
    amount: number;
    multiplier: number;
    name: string;
    color: string;
    timestamp: number;
  }>;
}

interface AuthState {
  user: User | null;
  userData: UserData | null;
  isAuthenticated: boolean;
  isGuest: boolean; // Track if user is in guest mode
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUserData: (data: Partial<UserData>) => Promise<void>;
  clearError: () => void;
  loginAsGuest: () => void;
}

const getErrorMessage = (error: AuthError) => {
  switch (error.code) {
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please try again.';
    case 'auth/user-not-found':
      return 'No account found with this email. Please sign up first.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account already exists with this email.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    default:
      return error.message;
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  userData: null,
  isAuthenticated: false,
  isGuest: false,
  isLoading: false,
  error: null,

  clearError: () => set({ error: null }),

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Fetch user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      const userData = userDoc.exists() ? userDoc.data() as UserData : null;

      set({
        user: userCredential.user,
        userData,
        isAuthenticated: true,
        isLoading: false
      });
    } catch (error) {
      set({
        error: getErrorMessage(error as AuthError),
        isLoading: false,
        isAuthenticated: false
      });
      throw error; // Re-throw to handle in the component
    }
  },

  signup: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Initialize user data in Firestore
      const initialUserData: UserData = {
        email,
        waterIntake: 0,
        dailyGoal: 2000, // Default goal: 2000ml
        streak: 0,
        lastLogin: new Date(), // Initialize lastLogin
        reminderInterval: 60, // Default reminder interval: 60 minutes
        lastCheckDate: '',
        checkInDates: [], // Initialize checkInDates
        monthlyStats: {},
        drinkHistory: [],
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), initialUserData);

      set({
        user: userCredential.user,
        userData: initialUserData,
        isAuthenticated: true,
        isLoading: false
      });
    } catch (error) {
      set({
        error: getErrorMessage(error as AuthError),
        isLoading: false,
        isAuthenticated: false
      });
      throw error; // Re-throw to handle in the component
    }
  },

  logout: async () => {
    try {
      set({ isLoading: true });
      
      // If it's a guest user, we don't need to call firebaseSignOut
      if (get().isGuest) {
        set({
          user: null,
          userData: null,
          isAuthenticated: false,
          isGuest: false,
          isLoading: false
        });
      } else {
        await firebaseSignOut(auth);
        set({
          user: null,
          userData: null,
          isAuthenticated: false,
          isGuest: false,
          isLoading: false
        });
      }
    } catch (error) {
      set({
        error: getErrorMessage(error as AuthError),
        isLoading: false
      });
    }
  },

  updateUserData: async (data: Partial<UserData>) => {
    const { user, userData, isGuest } = get();
    if (!user || !userData) return;
    
    // Don't update data for guest users
    if (isGuest) return;

    try {
      set({ isLoading: true });
      const updatedData = { ...userData, ...data };
      await setDoc(doc(db, 'users', user.uid), updatedData, { merge: true });
      set({
        userData: updatedData,
        isLoading: false
      });
    } catch (error) {
      set({
        error: getErrorMessage(error as AuthError),
        isLoading: false
      });
    }
  },
  
  loginAsGuest: () => {
    // Create a guest user object with basic data
    const guestUserData: UserData = {
      email: 'guest@hydrio.com',
      waterIntake: 0,
      dailyGoal: 2000, // Default goal: 2000ml
      streak: 0,
      lastLogin: new Date(),
      reminderInterval: 60, // Default reminder interval: 60 minutes
      lastCheckDate: '',
      checkInDates: [],
      monthlyStats: {},
      drinkHistory: [],
    };
    
    // Create a guest user with minimal required properties
    const guestUser = {
      uid: 'guest_user',
      email: 'guest@hydrio.com',
      displayName: 'Guest User',
      photoURL: null,
      emailVerified: false,
      isAnonymous: true,
      providerId: 'guest',
      refreshToken: '',
      metadata: {
        creationTime: new Date().toISOString(),
        lastSignInTime: new Date().toISOString(),
      },
      providerData: [],
      tenantId: null,
      phoneNumber: null,
      toJSON: () => ({}),
      getIdToken: async () => 'guest_token',
      getIdTokenResult: async () => {
        // Create a minimal IdTokenResult object
        const result: any = {
          token: 'guest_token',
          expirationTime: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
          authTime: new Date().toISOString(),
          issuedAtTime: new Date().toISOString(),
          signInProvider: 'guest',
          claims: {},
          signInSecondFactor: null,
          toJSON: () => ({})
        };
        return result;
      },
      reload: async () => {},
      delete: async () => {},
    };
    
    set({
      user: guestUser as unknown as User,
      userData: guestUserData,
      isAuthenticated: true,
      isGuest: true,
      isLoading: false,
      error: null
    });
  }
}));

// Set up auth state listener
onAuthStateChanged(auth, async (user) => {
  // Don't override guest state if user is in guest mode
  if (useAuthStore.getState().isGuest) return;
  
  if (user) {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() as UserData : null;
      useAuthStore.setState({
        user,
        userData,
        isAuthenticated: true,
        isGuest: false, // Ensure isGuest is false for real users
        isLoading: false
      });
    } catch (error) {
      useAuthStore.setState({
        error: getErrorMessage(error as AuthError),
        isLoading: false
      });
    }
  } else {
    // Only update state if not in guest mode
    if (!useAuthStore.getState().isGuest) {
      useAuthStore.setState({
        user: null,
        userData: null,
        isAuthenticated: false,
        isGuest: false,
        isLoading: false
      });
    }
  }
});